import { settings } from 'Js/config/settings';
import { OU_TYPE_CONFIG } from './typeConfig';
import { getNow, isAfter, isBeforeOrEqual } from '@kathondvla/sri-client/date-utils';
import { sriClient as kathOndVlaApi } from 'ReduxLoop/utils/apiConfig';

const samApi = require('@kathondvla/sri-client/fetch-sri-client')({
  baseUrl: settings.apis.samApi,
});

/* const kathOndVlaApi = require('@kathondvla/sri-client/fetch-sri-client')({
  baseUrl: settings.apis.kathOndVla,
}); */

let legalOrganisationalFormsPromise = null;
export const fetchLegalForms = async () => {
  if (!legalOrganisationalFormsPromise) {
    legalOrganisationalFormsPromise = samApi.getAll('/sam/commons/legalorganisationalforms');
  }
  return await legalOrganisationalFormsPromise;
};

let countryPromise = null;
export const fetchCountries = async () => {
  if (!countryPromise) {
    countryPromise = samApi.getAll('/sam/commons/countries');
  }
  return await countryPromise;
};

export const fetchOuData = async (ouHref) => {
  const domain = ouHref.split('/')[1];
  const ouPromise = samApi.get(ouHref);
  const cdPromise = samApi.getAll(`/${domain}/organisationalunits/contactdetails`, {
    organisationalUnit: ouHref,
  });
  const relationsFromPromise = samApi.getAll(`/${domain}/organisationalunits/relations`, {
    to: ouHref,
    expand: 'results.from',
  });
  const relationsToPromise = samApi.getAll(`/${domain}/organisationalunits/relations`, {
    from: ouHref,
    expand: 'results.to',
  });
  const locationsPromise = domain === 'sam' ? samApi.getAll(`/${domain}/organisationalunits/locations`, {
    organisationalUnit: ouHref,
    expand: 'results.physicalLocation',
  }) : null;
  const ouResource = await ouPromise;
  const ouTypeConfig = OU_TYPE_CONFIG[ouResource.type];
  const contactDetails = await cdPromise;
  const relationsFrom = await relationsFromPromise;
  const relationsTo = await relationsToPromise;
  const locations = domain === 'sam' ? await locationsPromise : [];
  const allIdentifierHrefs = [
    ouHref,
    ...relationsFrom.map((rel) => rel.from.href),
    ...relationsTo.map((rel) => rel.to.href),
  ];
  const identifiers = await samApi.getAllReferencesTo(
    `/${domain}/organisationalunits/externalIdentifiers`,
    { typeIn: 'INSTITUTION_NUMBER,COMPANY_NUMBER,VAT_NUMBER' },
    'organisationalUnit',
    allIdentifierHrefs
  );
  let legOrgForm = null;
  if (ouTypeConfig.components.legalForm) {
    const legalFormList = await fetchLegalForms();
    const legOrgForms = await samApi.getAll(`/${domain}/organisationalunits/legalorganisationalforms`, {
      organisationalUnit: ouHref,
    });
    if (legOrgForms.length > 0) {
      const samLegOrgForm = legalFormList.find((elem) => elem.$$meta.permalink === legOrgForms[0].legalOrganisationalForm.href);
      legOrgForm = {
        ...legOrgForms[0],
        legalOrganisationalForm: { href: legOrgForms[0].legalOrganisationalForm.href, $$expanded: samLegOrgForm },
      }
    }
  }
  return {
    ouResource,
    contactDetails,
    relations: [...relationsFrom, ...relationsTo],
    locations,
    identifiers,
    legalOrganisationalForm: legOrgForm,
  };
};

export const getOuByInstitutionNumber = async (institutionNumber) => {
  const results = await samApi.getList('/sam/organisationalunits/externalidentifiers', {
    value: institutionNumber,
    type: 'INSTITUTION_NUMBER',
  });
  if (results.length === 0) {
    return null;
  }
  return {
    $$meta: { permalink: results[0].organisationalUnit.href },
    institutionNumber,
  };
};

export const sendBatch = async (batch, validate) => {
  try {
    await samApi.put(`/batch${validate ? '?dryRun=true' : ''}`, batch);
  } catch (error) {
    console.error('ERROR OCCURED', error);
    throw error;
  }
};

export const validateInstitutionNumber = async (value) => {
  const existing = await samApi.getList('/sam/organisationalunits/externalidentifiers', {
    type: 'INSTITUTION_NUMBER',
    value,
  });
  if (existing.length > 0) {
    return [{ message: 'Er is al een instelling met dit instellingsnummer' }];
  }
  return [];
};

export const getAllPhysicallocationsInCity = async (cityHref, type) => {
  return samApi.getAll('/sam/physicallocations', {
    type,
    'address.cityHref': cityHref,
    limit: 5000,
  });
};

export const getCurrentRelationWithOu = async ({ domain, type, href, prop, date }) => {
  const rels = await samApi.getAll(`/${domain}/organisationalunits/relations`, {
    [prop]: href,
    type,
    _date: date,
    expand: `results.${prop === 'from' ? 'to' : 'from'}`,
  });
  if (rels.length > 1) {
    console.warn('Multiple relations found for', domain, type, href, prop, date);
    console.warn('Multiple relations', rels);
  }
  return rels[0];
};

export const getPreviousRelationWithOu = async ({ domain, type, href, prop, date }) => {
  const rels = await samApi.getAll(`/${domain}/organisationalunits/relations`, {
    [prop]: href,
    type,
    endDateBefore: date,
    expand: `results.${prop === 'from' ? 'to' : 'from'}`,
  });
  return rels.reduce((latest, current) => {
    return current.startDate > latest.startDate ? current : latest;
  }, rels[0]);
};

export const getNextRelationWithOu = async ({ domain, type, href, prop, date }) => {
  if (!date) {
    return undefined;
  }
  const rels = await samApi.getAll(`/${domain}/organisationalunits/relations`, {
    [prop]: href,
    type,
    startDateAfter: date,
    expand: `results.${prop === 'from' ? 'to' : 'from'}`,
  });
  return rels.reduce((earliest, current) => {
    return current.startDate < earliest.startDate ? current : earliest;
  }, rels[0]);
};

export const getResponsibilities = async (ouHref) => {
  const resps = await kathOndVlaApi.getAll('/responsibilities', {
    organisationalUnit: ouHref,
    limit: 5000,
  });

  const activeResp = resps.filter((resp) => isBeforeOrEqual(resp.startDate, getNow()) && isAfter(resp.endDate, getNow()));

  return {
    responsibilities: activeResp,
    historicalResponsibilities: resps.filter((resp) => !activeResp.includes(resp)),
  }
};

export const getWorkgroup = async (ouHref) => {
  try {
    const results = await kathOndVlaApi.getAll(`/workgroups`, { organisationalUnit: ouHref });
    return results.length > 0 ? results[0] : null;
  } catch (error) {
    if (error.status === 404) {
      // No workgroup found, return null
      return null;
    } else if (error.status === 401) {
      // TODO I get "code": "oauth.bearerAuthTokenInvalid", "msg": "Error decrypting bearer token.", don't see what is wrong with the api
      return { message: 'There is a workgroup, but you do not have access to it.' };
    }
    throw error;
  }
};

export const getViesOrg = async (countryCode, vatNumber) => {
  return await samApi.sendPayload(`/kathondvla/organisationalunits/externalidentifiers/vies-lookup`, {
    countryCode: countryCode.toUpperCase(),
    vatNumber,
  }, undefined, 'POST');
};

const openStreetMapClient = require('@kathondvla/sri-client/fetch-sri-client')({
  baseUrl: 'https://nominatim.openstreetmap.org',
});
export const getOpenStreetMapAddress = async (qAddress) => {
  return await openStreetMapClient.getRaw(`/search?q=${qAddress}&format=json&addressdetails=1&limit=1`);
};

export const getSubCityByZipCode = async (zipCode) => {
  const results = await samApi.getAll('/sam/commons/subcities', { zipCode });
  return results;
}
